import { addDays, isSameDay, startOfWeek } from 'date-fns';
import type { PostingLogRow } from '@/utils/PostingLog/types';

type WeekStartDay = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Status of a pivoted group row — unlike PostingStatus this also covers a week that is a mix of both, and
 * 'Not posted' for a group that has no errors of its own but wasn't posted because its batch was rejected.
 */
export type PivotRowStatus = 'Posted' | 'Failed' | 'Partial' | 'Not posted';

export interface PivotCell {
  date: Date;
  quantity: number;
  upliftValue: number;
  logIds: number[];
  hasPosted: boolean;
  hasFailed: boolean;
  /** Distinct errors for the day's lines. */
  errorMessages: string[];
}

export interface PivotedLineGroup {
  key: string;
  projectName: string;
  costCentreName: string;
  price: number;
  status: PivotRowStatus;
  cells: PivotCell[];
  totalQuantity: number;
  totalUplift: number;
  /** Every unposted line in the group, including 'Not posted' ones, so Retry resends them all. */
  failedLogIds: number[];
  /** Distinct errors across the whole week — the same error on several days is listed once. */
  errorMessages: string[];
}

/** A line's stored error is its distinct errors joined with '; '. */
const splitErrorMessages = (errorMessage?: string): string[] =>
  (errorMessage ?? '').split('; ').map((m) => m.trim()).filter(Boolean);

const emptyCell = (date: Date): PivotCell => ({
  date, quantity: 0, upliftValue: 0, logIds: [], hasPosted: false, hasFailed: false, errorMessages: [],
});

/**
 * Groups posting log lines by project/cost centre/rate and spreads each group's hours across
 * the days of the timesheet's week — mirroring the day-column layout of the Timesheet Entry
 * grid instead of one row per (day × project) line.
 *
 * Returns `null` when there's no resolvable week (e.g. no lines have a transaction date yet),
 * so callers can fall back to a flat list.
 */
export const pivotPostingLogLines = (
  lines: PostingLogRow[],
  weekEnding: Date | null,
  weekStartDay: WeekStartDay,
): PivotedLineGroup[] | null => {
  if (!weekEnding) return null;

  const weekStart = startOfWeek(weekEnding, { weekStartsOn: weekStartDay });
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const groups = new Map<string, PivotedLineGroup>();

  for (const line of lines) {
    const key = `${line.projectCode}|${line.costCentreCode}|${line.price}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        projectName: line.projectName,
        costCentreName: line.costCentreName,
        price: line.price,
        status: 'Posted',
        cells: days.map(emptyCell),
        totalQuantity: 0,
        totalUplift: 0,
        failedLogIds: [],
        errorMessages: [],
      };
      groups.set(key, group);
    }

    const dayIndex = line.transactionDate
      ? days.findIndex((d) => isSameDay(d, line.transactionDate as Date))
      : -1;
    if (dayIndex === -1) continue;

    const cell = group.cells[dayIndex];
    cell.quantity += line.quantity;
    cell.upliftValue += line.upliftValue;
    cell.logIds.push(line.logId);

    if (line.status === 'Failed') {
      group.failedLogIds.push(line.logId);
      for (const message of splitErrorMessages(line.errorMessage)) {
        cell.hasFailed = true;
        if (!cell.errorMessages.includes(message)) cell.errorMessages.push(message);
        if (!group.errorMessages.includes(message)) group.errorMessages.push(message);
      }
    } else {
      cell.hasPosted = true;
    }

    group.totalQuantity += line.quantity;
    group.totalUplift += line.upliftValue;
  }

  for (const group of groups.values()) {
    const anyUnposted = group.failedLogIds.length > 0;
    const anyPosted = group.cells.some((c) => c.hasPosted);
    const anyErrors = group.errorMessages.length > 0;
    group.status = !anyUnposted ? 'Posted' : anyPosted ? 'Partial' : anyErrors ? 'Failed' : 'Not posted';
  }

  return Array.from(groups.values());
};
